import React from 'react';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { CheckIcon, ArrowRightIcon, AlertCircleIcon, LeafIcon, DropletIcon, ActivityIcon, ZapIcon, ImageIcon } from 'lucide-react';
export function AdvertorialPage() {
  const CTA_URL = 'https://lymphoriax.glowliftessencemx.com/';
  const handleCtaClick = () => {
    window.location.href = CTA_URL;
  };
  return <div className="min-h-screen bg-[#fcfdfc] font-sans text-gray-800" data-id="element-1">
      {/* Top Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-3 px-4 sticky top-0 z-50 shadow-sm" data-id="element-2">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-3">
          <div className="flex items-center space-x-2" data-id="element-4">
            <div className="w-8 h-8 bg-green-700 rounded-full flex items-center justify-center text-white font-bold" data-id="element-5">
              W
            </div>
            <span className="font-bold text-gray-900 tracking-tight" data-id="element-6">
              Wellness Daily Staff
            </span>
          </div>
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-7">
            Updated 2026
          </span>
        </div>
      </div>

      <main className="max-w-[760px] mx-auto px-5 py-8 md:py-12" data-id="element-8">
        {/* Article Header */}
        <header className="mb-10" data-id="element-9">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-6 tracking-tight" data-id="element-10">
            Why So Many Women Are Waking Up Puffy (And Why It's Not Fat)
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed font-medium" data-id="element-11">
            If your face looks swollen in the morning, your rings feel tighter,
            and your body feels heavy for no reason — you're not alone.
          </p>
        </header>

        {/* Hero Image Placeholder */}
        <figure className="mb-10 mx-auto max-w-full text-center" data-id="element-12">
          <div className="relative overflow-hidden rounded-2xl shadow-md border border-gray-100 bg-gray-50 aspect-[16/9] flex items-center justify-center" data-id="element-13">
            <div className="text-center text-gray-400" data-id="element-14">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" data-id="element-15" />
              <p className="text-sm font-medium" data-id="element-16">Hero Image Placeholder</p>
              <p className="text-xs mt-1" data-id="element-17">
                Replace with your lifestyle / wellness image
              </p>
            </div>
          </div>
          <figcaption className="mt-3 text-sm text-gray-400 italic" data-id="element-18">
            Millions of women deal with unexplained puffiness every morning.
          </figcaption>
        </figure>

        {/* Opening Story */}
        <section className="mb-10 prose prose-lg prose-gray max-w-none" data-id="element-19">
          <p className="mb-4" data-id="element-20">
            It starts small. Maybe you notice it in photos first — your face
            looks a little rounder, your jawline less defined. Or perhaps it's
            the way your favorite rings suddenly refuse to slide off at the end
            of the day.
          </p>
          <p className="mb-4" data-id="element-21">
            You look in the mirror and something feels off. You haven't gained
            weight, at least not the kind that comes from overeating. But you
            feel... heavy. Sluggish. Like you're wearing a suit that's slightly
            too tight.
          </p>
          <p data-id="element-22">
            It's not always weight. For thousands of women, this daily struggle
            isn't about fat cells — it's about fluid. And it's leaving them
            feeling disconnected from their own bodies.
          </p>
        </section>

        {/* Emotional Quote #1 */}
        <section className="mb-10" data-id="element-23">
          <blockquote className="bg-white border-l-4 border-green-400 p-6 md:p-8 rounded-r-xl shadow-sm italic text-lg md:text-xl text-gray-700 leading-relaxed" data-id="element-24">
            "I wasn't even eating badly… but I kept waking up swollen. My face
            looked tired, my jeans felt tighter, and I stopped wanting photos."
          </blockquote>
        </section>

        {/* Symptoms Box */}
        <section className="mb-12" data-id="element-25">
          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 md:p-8 rounded-r-lg shadow-sm" data-id="element-26">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center" data-id="element-27">
              <AlertCircleIcon className="w-6 h-6 text-amber-500 mr-2" data-id="element-28" />
              Common signs women notice:
            </h3>
            <ul className="space-y-3" data-id="element-29">
              {['Puffy face in the morning', 'Sock marks that linger', 'Rings suddenly feeling tight', 'Random bloating even when eating clean', 'Heavy sluggish "stuck" feeling'].map((item, i) => <li key={i} className="flex items-start text-gray-800 font-medium" data-id="element-30">
                  <span className="mr-3 text-amber-500 text-xl" data-id="element-31">•</span>
                  {item}
                </li>)}
            </ul>
          </div>
        </section>

        {/* Pattern Interrupt: It's Not Weight — It's Water */}
        <section className="mb-12" data-id="element-32">
          <div className="bg-green-50 border border-green-200 p-6 md:p-8 rounded-xl text-center shadow-sm" data-id="element-33">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3" data-id="element-34">
              It's Not Weight — It's Water
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed max-w-xl mx-auto" data-id="element-35">
              Many women think it's weight gain… But wellness experts say it's
              often <strong data-id="element-36">fluid retention + sluggish drainage</strong>,
              especially after 30.
            </p>
          </div>
        </section>

        {/* CTA 1 */}
        <div className="my-12 text-center" data-id="element-37">
          <Button onClick={handleCtaClick} variant="cta" size="xl" className="w-full md:w-auto px-8 py-6 text-lg md:text-xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1" data-id="element-38">
            See the Official Offer Page{' '}
            <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-39" />
          </Button>
          <p className="mt-3 text-sm text-gray-500 flex items-center justify-center" data-id="element-40">
            <CheckIcon className="w-4 h-4 text-green-600 mr-1" data-id="element-41" /> Free Shipping
            Worldwide • 90-Day Guarantee
          </p>
        </div>

        {/* Why Quick Fixes Don't Work */}
        <section className="mb-12" data-id="element-42">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-43">
            Why Quick Fixes Don't Work
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed" data-id="element-44">
            <p data-id="element-45">
              We've all been there. The desperate search for relief leads to
              extreme measures that often backfire.
            </p>
            <ul className="space-y-4 mt-4 pl-2" data-id="element-46">
              <li className="flex items-start" data-id="element-47">
                <span className="font-bold text-red-500 mr-2" data-id="element-48">✗</span>
                <span data-id="element-49">
                  <strong data-id="element-50">Detox teas</strong> often lead to uncomfortable
                  bathroom emergencies and dehydration, not true balance.
                </span>
              </li>
              <li className="flex items-start" data-id="element-51">
                <span className="font-bold text-red-500 mr-2" data-id="element-52">✗</span>
                <span data-id="element-53">
                  <strong data-id="element-54">Dry brushing</strong> feels good but requires
                  consistency that most busy women can't maintain daily.
                </span>
              </li>
              <li className="flex items-start" data-id="element-55">
                <span className="font-bold text-red-500 mr-2" data-id="element-56">✗</span>
                <span data-id="element-57">
                  <strong data-id="element-58">Lymphatic massages</strong> are effective but
                  expensive and only offer temporary relief.
                </span>
              </li>
              <li className="flex items-start" data-id="element-59">
                <span className="font-bold text-red-500 mr-2" data-id="element-60">✗</span>
                <span data-id="element-61">
                  <strong data-id="element-62">Extreme cleanses</strong> are unsustainable and can
                  leave you feeling depleted rather than energized.
                </span>
              </li>
            </ul>
            <p className="mt-4 italic text-gray-600 border-l-2 border-gray-300 pl-4" data-id="element-63">
              "I tried everything to get rid of the puffiness. I just wanted to
              look like myself again without starving myself or spending hours
              on treatments."
            </p>
          </div>
        </section>

        {/* The Mirror Test */}
        <section className="mb-12" data-id="element-64">
          <div className="bg-white border border-gray-200 p-6 md:p-8 rounded-2xl shadow-md text-center" data-id="element-65">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-66">
              The Mirror Test
            </h2>
            <p className="text-lg text-gray-700 mb-5" data-id="element-67">
              If you've ever thought:
            </p>
            <div className="space-y-3 max-w-md mx-auto text-left mb-6" data-id="element-68">
              <p className="text-lg italic text-gray-700 bg-gray-50 p-3 rounded-lg" data-id="element-69">
                "Why do I look swollen today?"
              </p>
              <p className="text-lg italic text-gray-700 bg-gray-50 p-3 rounded-lg" data-id="element-70">
                "Why do my rings feel tight?"
              </p>
              <p className="text-lg italic text-gray-700 bg-gray-50 p-3 rounded-lg" data-id="element-71">
                "Why do I feel heavier even when I eat clean?"
              </p>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-1" data-id="element-72">
              You're not imagining it.
            </p>
            <p className="text-lg font-semibold text-gray-900 mb-1" data-id="element-73">
              You're not alone.
            </p>
            <p className="text-lg text-green-700 font-bold mt-4" data-id="element-74">
              This is what women are finally addressing.
            </p>
          </div>
        </section>

        {/* Curiosity Bridge */}
        <section className="mb-6 text-center" data-id="element-75">
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed" data-id="element-76">
            Sleep experts say it's often not fat…
          </p>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-2" data-id="element-77">
            It's what happens when fluid gets <strong data-id="element-78">"stuck"</strong> and your
            body doesn't clear it efficiently.
          </p>
        </section>

        {/* Product Reveal */}
        <section className="mb-14 bg-green-50/50 p-8 rounded-2xl border border-green-100" data-id="element-79">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-2" data-id="element-80">
            The Simple Drop Ritual Women Are Calling a "Reset"
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-lg mx-auto" data-id="element-81">
            A gentle approach to feeling lighter and more vibrant every day.
          </p>

          <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-82">
            <div className="w-full md:w-1/2 flex justify-center" data-id="element-83">
              {/* Product Image Placeholder */}
              <div className="relative w-64 h-80 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100 overflow-hidden" data-id="element-84">
                <div className="text-center text-gray-400 z-10 p-4" data-id="element-85">
                  <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-40" data-id="element-86" />
                  <span className="block font-serif text-2xl text-green-900 font-bold" data-id="element-87">
                    LymphoriaX™
                  </span>
                  <span className="text-xs text-green-700 uppercase tracking-widest block mt-1" data-id="element-88">
                    Botanical Drops
                  </span>
                  <p className="text-xs text-gray-400 mt-3" data-id="element-89">
                    Replace with product photo
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-white opacity-30" data-id="element-90"></div>
              </div>
            </div>

            <div className="w-full md:w-1/2" data-id="element-91">
              <p className="text-lg text-gray-800 mb-4" data-id="element-92">
                <strong data-id="element-93">LymphoriaX™</strong> is a gentle botanical drop blend
                designed to support:
              </p>
              <ul className="space-y-3" data-id="element-94">
                {['Healthy fluid balance', 'Natural circulation', 'Daily drainage comfort', 'A lighter, more defined feeling'].map((benefit, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-95">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-96">
                      <CheckIcon className="w-3.5 h-3.5 text-green-700" data-id="element-97" />
                    </div>
                    {benefit}
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA 2 */}
        <div className="my-12 text-center" data-id="element-98">
          <Button onClick={handleCtaClick} variant="cta" size="xl" className="w-full md:w-auto px-8 py-6 text-lg md:text-xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1" data-id="element-99">
            See the Official Offer Page{' '}
            <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-100" />
          </Button>
          <p className="mt-3 text-sm text-gray-500 flex items-center justify-center" data-id="element-101">
            <CheckIcon className="w-4 h-4 text-green-600 mr-1" data-id="element-102" /> Free Shipping
            Worldwide • 90-Day Guarantee
          </p>
        </div>

        {/* Ingredients Section */}
        <section className="mb-14" data-id="element-103">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-104">
            Powered by Nature's Most Potent Botanicals
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-105">
            <Card className="border-green-100 bg-white hover:shadow-md transition-shadow" data-id="element-106">
              <CardHeader className="pb-2" data-id="element-107">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3 text-green-700" data-id="element-108">
                  <LeafIcon className="w-6 h-6" data-id="element-109" />
                </div>
                <CardTitle className="text-lg text-green-900" data-id="element-110">
                  Cleavers Aerial Parts
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-111">
                <p className="text-gray-600" data-id="element-112">
                  Traditionally used to reduce stubborn swelling and support the
                  body's natural cleansing channels.
                </p>
              </CardContent>
            </Card>

            <Card className="border-green-100 bg-white hover:shadow-md transition-shadow" data-id="element-113">
              <CardHeader className="pb-2" data-id="element-114">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center mb-3 text-pink-700" data-id="element-115">
                  <DropletIcon className="w-6 h-6" data-id="element-116" />
                </div>
                <CardTitle className="text-lg text-green-900" data-id="element-117">
                  Red Clover Blossom
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-118">
                <p className="text-gray-600" data-id="element-119">
                  Known for its ability to help reduce facial puffiness and
                  promote a clearer complexion.
                </p>
              </CardContent>
            </Card>

            <Card className="border-green-100 bg-white hover:shadow-md transition-shadow" data-id="element-120">
              <CardHeader className="pb-2" data-id="element-121">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3 text-amber-700" data-id="element-122">
                  <ActivityIcon className="w-6 h-6" data-id="element-123" />
                </div>
                <CardTitle className="text-lg text-green-900" data-id="element-124">
                  Stillingia Root
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-125">
                <p className="text-gray-600" data-id="element-126">
                  A powerful root that helps open lymph pathways to keep fluids
                  moving freely.
                </p>
              </CardContent>
            </Card>

            <Card className="border-green-100 bg-white hover:shadow-md transition-shadow" data-id="element-127">
              <CardHeader className="pb-2" data-id="element-128">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3 text-blue-700" data-id="element-129">
                  <ZapIcon className="w-6 h-6" data-id="element-130" />
                </div>
                <CardTitle className="text-lg text-green-900" data-id="element-131">
                  Prickly Ash Bark
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-132">
                <p className="text-gray-600" data-id="element-133">
                  Works to energize slow circulation, helping to flush out
                  stagnation.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What Women Notice First */}
        <section className="mb-12 bg-gray-50 p-8 rounded-2xl" data-id="element-134">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-135">
            What Women Notice First
          </h2>
          <p className="text-center text-gray-600 mb-8" data-id="element-136">
            Most women report within the first week:
          </p>

          <div className="space-y-4 max-w-md mx-auto" data-id="element-137">
            {['Less puffiness in the morning', 'Less bloating and heaviness', 'Clothes fitting more comfortably', 'More energy and confidence', 'Feeling like themselves again'].map((result, i) => <div key={i} className="flex items-center bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-138">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-139">
                  <CheckIcon className="w-4 h-4 text-white" data-id="element-140" />
                </div>
                <span className="font-medium text-gray-800" data-id="element-141">{result}</span>
              </div>)}
          </div>
        </section>

        {/* Emotional Quote #2 */}
        <section className="mb-12" data-id="element-142">
          <blockquote className="bg-white border-l-4 border-green-400 p-6 md:p-8 rounded-r-xl shadow-sm italic text-lg md:text-xl text-gray-700 leading-relaxed" data-id="element-143">
            "People asked what changed… I didn't even diet. I just finally felt
            light again."
          </blockquote>
        </section>

        {/* Closing Section */}
        <section className="mb-12 text-center max-w-2xl mx-auto" data-id="element-144">
          <p className="text-xl md:text-2xl font-serif italic text-gray-800 mb-6" data-id="element-145">
            "This isn't about becoming someone else… It's about getting back to
            you."
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-146">
            Imagine waking up and recognizing the face in the mirror
            immediately. Imagine putting on your rings without struggle. Imagine
            moving through your day feeling light, confident, and comfortable in
            your own skin.
          </p>
        </section>

        {/* CTA 3 */}
        <div className="my-12 text-center" data-id="element-147">
          <Button onClick={handleCtaClick} variant="cta" size="xl" className="w-full md:w-auto px-8 py-6 text-lg md:text-xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1" data-id="element-148">
            See the Official Offer Page{' '}
            <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-149" />
          </Button>
          <p className="mt-3 text-sm text-gray-500 flex items-center justify-center" data-id="element-150">
            <CheckIcon className="w-4 h-4 text-green-600 mr-1" data-id="element-151" /> Free Shipping
            Worldwide • 90-Day Guarantee
          </p>
        </div>

        <hr className="border-gray-200 my-12" data-id="element-152" />

        {/* Footer */}
        <footer className="text-center text-gray-400 text-xs md:text-sm pb-12" data-id="element-153">
          <p className="mb-4" data-id="element-154">
            This product is not intended to diagnose, treat, cure, or prevent
            any disease. Individual results may vary.
          </p>
          <p data-id="element-155">© 2026 Wellness Daily Staff. All rights reserved.</p>
        </footer>
      </main>
    </div>;
}